import styled from '@emotion/styled';

// `ActAsModalStylesWrapper` was deleted here on 2026-08-10. It was a `styled.div`
// whose only content was DESCENDANT selectors for ActAsModal's dialog chrome --
// which meant the dialog had to render INSIDE it, which is the entire reason
// ActAsModal passed rc-dialog `getContainer={false}`.
//
// That cost more than it looked like: rc-dialog's scroll lock is gated on its
// lock flag and not on whether it portals, so the modal locked `body` regardless
// and narrowed it to compensate -- and a `position: fixed` nav sizes to the
// viewport, not to body, so the top nav jumped sideways by the scrollbar width
// on every open.
//
// The rules now live in `./actAsModal.scss`, scoped to the modal's own global
// class names, so the dialog portals like every other overlay.

/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const ActionNav = styled.div`
	display: flex;
	flex-wrap: nowrap;
	padding-left: 0;
	margin-bottom: 0;
	list-style: none;
	justify-content: flex-end;
	align-items: center;
	width: 100%;
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const BayNav = styled.div<{ $platformType?: string; $actorAccount?: boolean }>`
	position: fixed;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	align-items: center;
	justify-content: space-between;
	height: 50px;
	padding-bottom: 0;
	/* Offset by the User Tour top-of-page banner (in normal flow above everything). */
	top: var(--usertour-banner-height, 0px);
	right: 0;
	left: 0;
	padding: 0.25rem 1rem;
	background-color: var(--bs-white);
	z-index: 100 !important;
	transition: width, left, right, 0.3s;

	/* The "Act As" banner's height is dynamic (the acting-as name can wrap, the
	   font swaps in after first paint, the viewport rotates/zooms), so ActAsBanner
	   measures it and publishes --actas-banner-height. Anchor the nav to that live
	   value so it sits flush at every width for every banner variant (purple,
	   yellow-social, yellow-client). When no Act As banner is mounted (e.g. only
	   the free-trial banner is showing), the variable is unset and the literal in
	   the var() fallback is used instead, matching the previous fixed offset.

	   The -2px tucks the nav under the banner. The banner and nav are two separate
	   position:fixed layers, so a sub-pixel boundary between them leaves a hairline
	   that scrolling page content shows through. The banner has a higher z-index
	   (999 vs the nav's 99/100), so overlapping the nav upward lets the banner's own
	   background/border cover the seam — visually identical, but no gap can exist. */
	&.banner-single {
		margin-top: calc(var(--actas-banner-height, 45px) - 2px);
	}

	@media only screen and (max-width: 575px) {
		${(props) => props.$platformType === 'client' && `
			&.banner-single {
				margin-top: calc(var(--actas-banner-height, 85px) - 2px) !important;
			}
		`}
		${(props) => props.$platformType === 'social' && `
			&.banner-single {
				margin-top: calc(var(--actas-banner-height, 82px) - 2px);
			}
		`}
	}

	&.banner-double {
		margin-top: 90px;
	}

	&.launchpad-hovered {
		z-index: 1099 !important;
	}

	.balance-banner-text {
		font-size: 12px;
	}

	@media only screen and (max-width: 767px) {
		z-index: 99;
	}

	@media only screen and (min-width: 480px) {
		.balance-banner-text {
			font-size: 1rem;
		}
	}

	@media only screen and (min-width: 768px) {
		flex-direction: row;
	}

	.launchpad-btn-spacing {
		@media only screen and (max-width: 991px) {
			margin-left: 2rem;
		}
	}
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const StyledNavButton = styled.button<{ $isActive?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 0.25rem;
	margin-right: 0.85rem;
	padding: 0.86rem !important;
	position: relative;
	color: var(--bs-gray-dark) !important;
	border: 0;
	background-color: var(--bs-white);

	&:not(.disabled):hover {
		background-color: var(--bs-100);
	}

	${(props) => props.$isActive && `
		background-color: var(--bs-100);
	`}

	&.sms-button {
		display: none;
	}

	&.bell-animation.animate {
		animation: wiggle 1s ease-in-out;
		animation-delay: 0s;
		animation-iteration-count: 1;
	}

	@keyframes wiggle {
		0% {
			transform: rotate(0);
		}
		25% {
			transform: rotate(-10deg);
		}
		50% {
			transform: rotate(10deg);
		}
		75% {
			transform: rotate(-10deg);
		}
		100% {
			transform: rotate(0);
		}
	}

	@media only screen and (max-width: 991px) {
		margin: 0 0 0 0.5rem;

		&.notification-button {
			margin: 0 !important;
			margin-right: 0.5rem !important;
		}

		&.desktop-menu-icons {
			display: none;
		}

		&.sms-button {
			display: flex !important;
		}
	}
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const StyledBadge = styled.span`
	display: inline-block;
	padding: 0.25rem;
	font-size: 0.8em;
	font-weight: 600;
	line-height: 1.2;
	color: #fff;
	text-align: center;
	white-space: nowrap;
	vertical-align: baseline;
	border-radius: 50rem;
	background-color: var(--bs-danger);
	position: absolute;
	top: -1px;
	right: -5px;
	height: 21px;
	min-width: 22px;
	opacity: 0;
	transform: scale(0);
	animation: showCount 0.5s ease-in-out forwards;
	animation-delay: 1.2s;

	&.animate {
		opacity: 0;
		transform: scale(0);
		animation: showCount 0.5s ease-in-out forwards;
		animation-delay: 1.2s;
	}

	@keyframes showCount {
		0% {
			opacity: 0;
			transform: scale(0);
		}
		100% {
			opacity: 1;
			transform: scale(1);
		}
	}
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const DividerDiv = styled.div<{ showDivider?: boolean }>`
	border-left: 1px solid var(--bs-200);
	height: 24px;
	margin: 8px 16px;
	@media only screen and (max-width: 991px) {
		&.desktop-menu-icons {
			display: none;
		}
	}
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const DrawerHeaderSpan = styled.span`
	font-size: 18px;
	font-weight: 500;
	line-height: 22px;
	padding-left: 0.5rem;
	text-transform: capitalize;
	display: flex;
	align-items: center;
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const FilterIcon = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: var(--bs-200);
	border-radius: 50%;
	margin: 0 0 0 16px;
	padding: 3px 7px !important;
	height: 32px;
	width: 32px;
	position: relative;

	&.pink-bg {
		background-color: var(--bs-pink-500);

		&:hover,
		&:focus {
			background-color: var(--bs-pink-600) !important;
		}
	}

	&.sms-button {
		display: none;
	}

	@media only screen and (max-width: 767px) {
		margin: 0 0 0 8px;
	}

	@media only screen and (min-width: 768px) {
		&.sms-button {
			display: flex !important;
		}
	}
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const FeatureMenuHeader = styled.div`
	width: 100%;
	.title {
		padding-left: 5px;
		margin-top: 10px;
	}
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const FeatureMenuFooter = styled.div`
	text-align: center;
`;
/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const LaunchpadButtonWrapper = styled.div`
	&:hover {
		.bay-nav {
			z-index: 1099 !important;
		}
	}

	@media only screen and (min-width: 768px) {
		min-width: 140px;
	}
`;
